import { useMemo } from "react";
import { CriticalPeriodDef, CriticalPeriodKind } from "../../../../models/chart_model";

export interface PeriodRun {
  kind: CriticalPeriodKind;
  startIndex: number;
  endIndex: number;
}

export interface CriticalPeriodBand {
  kind: CriticalPeriodKind;
  label: string;
  icon: string;
  color: string;
  fillColor: string;
  startHour: number;
  endHour: number;
  gradientStops: { offset: number; color: string }[];
  pointFill: (string | null)[];
  runs: PeriodRun[];
  stats?: Record<string, string>;
}

export const KIND_DEFAULTS: Record<
  CriticalPeriodKind,
  {
    icon: string;
    color: string;
    fill: string;
    startHour: number;
    endHour: number;
    gradientStops: { offset: number; color: string }[];
  }
> = {
  solar: {
    icon: "☀",
    color: "#f59e0b",
    fill: "rgba(251,191,36,0.07)",
    startHour: 10,
    endHour: 16,
    gradientStops: [
      { offset: 0, color: "rgba(251,191,36,0.15)" },
      { offset: 1, color: "rgba(251,191,36,0.01)" },
    ],
  },
  evening_ramp: {
    icon: "⚡",
    color: "#f97316",
    fill: "rgba(249,115,22,0.06)",
    startHour: 17,
    endHour: 21,
    gradientStops: [
      { offset: 0, color: "rgba(249,115,22,0.13)" },
      { offset: 1, color: "rgba(249,115,22,0.01)" },
    ],
  },
  peak_demand: {
    icon: "▲",
    color: "#ef4444",
    fill: "rgba(239,68,68,0.05)",
    startHour: 18,
    endHour: 23,
    gradientStops: [
      { offset: 0, color: "rgba(239,68,68,0.11)" },
      { offset: 1, color: "rgba(239,68,68,0.01)" },
    ],
  },
  valley: {
    icon: "🌙",
    color: "#3b82f6",
    fill: "rgba(59,130,246,0.05)",
    startHour: 0,
    endHour: 5,
    gradientStops: [
      { offset: 0, color: "rgba(59,130,246,0.11)" },
      { offset: 1, color: "rgba(59,130,246,0.01)" },
    ],
  },
};

type LabelFormat = "time" | "block48" | "block96" | "unknown";

function detectLabelFormat(samples: (string | number)[]): LabelFormat {
  for (const raw of samples) {
    if (raw === null || raw === undefined || raw === "") continue;
    if (typeof raw === "number" || /^\d+$/.test(String(raw))) {
      const max = Math.max(
        ...samples.map((x) => Number(x)).filter(Number.isFinite),
      );
      if (max <= 48) return "block48";
      if (max <= 96) return "block96";
      return "unknown";
    }
    const s = String(raw);
    if (/^\d{1,2}:\d{2}$/.test(s)) return "time";
    if (/T\d{2}:/.test(s)) return "time";
    if (/\d{4}-\d{2}-\d{2} \d{2}:/.test(s)) return "time";
  }
  return "unknown";
}

function hourOf(value: string | number, format: LabelFormat): number | null {
  if (format === "time") {
    const s = String(value);
    const m = s.match(/^(\d{1,2}):/) || s.match(/T(\d{2}):/) || s.match(/\d{4}-\d{2}-\d{2} (\d{2}):/);
    return m ? parseInt(m[1], 10) : null;
  }
  if (format === "block48" || format === "block96") {
    const b = Number(value);
    if (!Number.isFinite(b) || b < 1) return null;
    return format === "block48" ? Math.floor((b - 1) / 2) : Math.floor((b - 1) / 4);
  }
  return null;
}

function hourInPeriod(h: number, start: number, end: number): boolean {
  if (start <= end) return h >= start && h < end;
  return h >= start || h < end;
}

export function useCriticalPeriods(
  periods: CriticalPeriodDef[] | undefined,
  xAxisData: (string | number)[],
  activePeriodKinds: Set<CriticalPeriodKind>,
): { bands: CriticalPeriodBand[]; supported: boolean } {
  return useMemo(() => {
    if (!periods || periods.length === 0 || xAxisData.length === 0) {
      return { bands: [], supported: false };
    }
    const samples = xAxisData.filter((v) => v !== null && v !== undefined && v !== "").slice(0, 12);
    const format = detectLabelFormat(samples);
    if (format === "unknown") return { bands: [], supported: false };

    const hours = xAxisData.map((v) => hourOf(v as string | number, format));
    const supported = hours.some((h) => h !== null);
    if (!supported) return { bands: [], supported: false };

    const bands: CriticalPeriodBand[] = [];

    for (const p of periods) {
      if (!activePeriodKinds.has(p.kind)) continue;
      const def = KIND_DEFAULTS[p.kind];
      const color = p.color ?? def.color;

      const pointFill: (string | null)[] = new Array(xAxisData.length).fill(null);
      const runs: PeriodRun[] = [];
      let runStart = -1;

      for (let i = 0; i < hours.length; i++) {
        const h = hours[i];
        const inside = h !== null && hourInPeriod(h, p.startHour, p.endHour);
        if (inside) {
          pointFill[i] = def.fill;
          if (runStart === -1) runStart = i;
        } else if (runStart !== -1) {
          runs.push({ kind: p.kind, startIndex: runStart, endIndex: i - 1 });
          runStart = -1;
        }
      }
      if (runStart !== -1) {
        runs.push({ kind: p.kind, startIndex: runStart, endIndex: hours.length - 1 });
      }
      if (runs.length === 0) continue;

      bands.push({
        kind: p.kind,
        label: p.label,
        icon: def.icon,
        color,
        fillColor: def.fill,
        startHour: p.startHour,
        endHour: p.endHour,
        gradientStops: def.gradientStops,
        pointFill,
        runs,
        stats: p.stats,
      });
    }

    return { bands, supported };
  }, [periods, xAxisData, activePeriodKinds]);
}
